const inactiveStatuses = new Set([
  "cancelled",
  "canceled",
  "rejected",
  "refunded",
  "declined",
  "removed",
]);

export const asPartnerList = (value) =>
  Array.isArray(value) ? value : Object.values(value || {});

export function isConfirmedPartnerPayment(item) {
  const status = String(item?.status || "").toLowerCase();
  const paymentStatus = String(
    item?.payment?.status || item?.paymentStatus || "",
  ).toLowerCase();
  if (inactiveStatuses.has(status)) return false;
  return (
    paymentStatus === "paid" ||
    (status === "confirmed" &&
      ["not_required", "not-required"].includes(paymentStatus))
  );
}

export function getPartnerSelections(registration, kind) {
  const sources =
    kind === "lodging"
      ? [
          registration?.lodging,
          registration?.accommodation,
          registration?.lodgings,
        ]
      : [registration?.meals, registration?.meal];
  const selected = new Map();
  const add = (entry) => {
    if (Array.isArray(entry)) {
      entry.forEach(add);
      return;
    }
    if (entry && typeof entry === "object") {
      if (entry.id) selected.set(String(entry.id), entry);
      return;
    }
    if (entry && entry !== "none") selected.set(String(entry), null);
  };
  sources.filter(Boolean).forEach((value) => {
    add(value.global);
    add(value.globalId);
    add(value.selected);
    Object.values(value.byDay || value.days || {}).forEach(add);
    Object.values(value.byDayUnits || {}).forEach((units) => {
      if (units && typeof units === "object") {
        Object.keys(units).forEach((id) => selected.set(String(id), units[id]));
      }
    });
  });
  return selected;
}

export const getServicePartnerWaypointId = (item) =>
  String(
    item?.partnerWaypointId || item?.waypointId || item?.partnerId || "",
  );

export function getPartnerSelectionAmount(selection, service) {
  const value = selection?.totalPrice ?? selection?.price;
  return Number.isFinite(Number(value))
    ? Math.max(0, Number(value))
    : Math.max(0, Number(service?.price) || 0);
}

export function isEventGuideUser(event, uid) {
  const target = String(uid || "");
  if (!target) return false;
  const ids = [
    event?.guideId,
    event?.guideUid,
    event?.coGuideId,
    event?.coGuideUid,
  ];
  asPartnerList(event?.coGuides).forEach((guide) =>
    ids.push(guide?.uid || guide?.id || guide),
  );
  return ids.some((id) => String(id || "") === target);
}

function waypointLookup(waypoints) {
  const lookup = new Map();
  Object.entries(waypoints || {}).forEach(([firebaseKey, waypoint]) => {
    [firebaseKey, waypoint?.id].filter(Boolean).forEach((id) =>
      lookup.set(String(id), { firebaseKey, ...(waypoint || {}) }),
    );
  });
  return lookup;
}

function eventServices(event) {
  const services = [];
  const add = (items, kind, date) =>
    asPartnerList(items).forEach((item) => {
      const id = String(item?.id || "");
      const partnerWaypointId = getServicePartnerWaypointId(item);
      if (!id || !partnerWaypointId) return;
      services.push({
        ...item,
        id,
        kind,
        date,
        partnerWaypointId,
        price: Math.max(0, Number(item?.price) || 0),
      });
    });
  add(event?.globalLodgings, "lodging", "Todo o evento");
  add(event?.globalMeals, "meals", "Todo o evento");
  asPartnerList(event?.meetingPoints).forEach((day, index) => {
    const date = day?.date || `Dia ${index + 1}`;
    add(day?.lodgings, "lodging", date);
    add(day?.meals, "meals", date);
  });
  return services;
}

const moneyRound = (value) => Math.round((Number(value) || 0) * 100) / 100;

const stripeFeeShare = (participant, gross, chargeStripeFee) => {
  if (!chargeStripeFee) return 0;
  const payment = participant?.payment || {};
  if (String(payment?.provider || "").toLowerCase() !== "stripe") return 0;
  const fee = Math.max(0, Number(payment?.stripeFeeAmount) || 0);
  const paidTotal = Math.max(
    0,
    Number(payment?.amountTotal ?? participant?.pricing?.total ?? participant?.finalPrice) || 0,
  );
  if (!fee || !paidTotal || !gross) return 0;
  return moneyRound(fee * Math.min(1, gross / paidTotal));
};

export function buildEventPartnerPaymentRows({
  events = {},
  registrations = {},
  waypoints = {},
  users = {},
  payouts = {},
} = {}) {
  const waypointById = waypointLookup(waypoints);
  const detailRows = [];

  Object.entries(events || {}).forEach(([eventId, event]) => {
    const services = eventServices(event);
    if (!services.length) return;

    const lodgingPartners = new Set(
      services
        .filter((service) => service.kind === "lodging")
        .map((service) =>
          String(
            waypointById.get(service.partnerWaypointId)?.partnerUserUid ||
              service?.partnerUserUid ||
              "",
          ),
        )
        .filter(Boolean),
    );

    Object.entries(registrations?.[eventId] || {}).forEach(
      ([registrationUid, registration]) => {
        if (!isConfirmedPartnerPayment(registration)) return;
        const participants = [
          { ...registration, uid: registration?.uid || registrationUid },
        ];
        Object.values(registration?.invitees || {}).forEach((invitee) => {
          if (isConfirmedPartnerPayment(invitee)) participants.push(invitee);
        });

        participants.forEach((participant) => {
          const selected = {
            lodging: getPartnerSelections(participant, "lodging"),
            meals: getPartnerSelections(participant, "meals"),
          };
          services.forEach((service) => {
            if (!selected[service.kind].has(service.id)) return;
            const waypoint = waypointById.get(service.partnerWaypointId) || {};
            const partnerUid = String(
              waypoint?.partnerUserUid || service?.partnerUserUid || "",
            );
            if (!partnerUid) return;
            const selection = selected[service.kind].get(service.id);
            const freeGuideLodging =
              service.kind === "lodging" &&
              isEventGuideUser(event, participant?.uid);
            const gross = freeGuideLodging
              ? 0
              : getPartnerSelectionAmount(selection, service);
            const discountPercent = Math.max(
              0,
              Math.min(100, Number(waypoint?.partnerAppDiscountPercent) || 0),
            );
            const discount = moneyRound((gross * discountPercent) / 100);
            const stripeFee = stripeFeeShare(
              participant,
              gross,
              waypoint?.partnerChargeStripeFee === true,
            );
            const net = moneyRound(Math.max(0, gross - discount - stripeFee));
            // Se o mesmo parceiro também fornece alojamento neste evento, as
            // refeições fazem parte do acerto do alojamento. A área Refeições
            // fica reservada a parceiros exclusivamente alimentares.
            const payoutKind =
              service.kind === "meals" && !lodgingPartners.has(partnerUid)
                ? "meals"
                : "lodging";
            detailRows.push({
              eventId,
              serviceId: service.id,
              partnerWaypointId: service.partnerWaypointId,
              eventName: event?.name || event?.title || eventId,
              participantUid: String(participant?.uid || ""),
              partnerUid,
              partnerName:
                waypoint?.accommodationBrandName ||
                waypoint?.name ||
                users?.[partnerUid]?.name ||
                users?.[partnerUid]?.email ||
                partnerUid,
              payoutKind,
              serviceKind: service.kind,
              serviceName: service?.name ||
                (service.kind === "lodging" ? "Alojamento" : "Refeição"),
              participantName:
                participant?.name ||
                participant?.displayName ||
                participant?.email ||
                "Participante",
              date: service.date,
              gross,
              discountPercent,
              discount,
              stripeFee,
              net,
            });
          });
        });
      },
    );
  });

  const grouped = new Map();
  detailRows.forEach((row) => {
    const payoutId = `${row.eventId}__${row.payoutKind}`;
    const key = `${row.partnerUid}|${payoutId}`;
    const current = grouped.get(key) || {
      payoutId,
      eventId: row.eventId,
      eventName: row.eventName,
      partnerUid: row.partnerUid,
      partnerName: row.partnerName,
      kind: row.payoutKind,
      gross: 0,
      discount: 0,
      stripeFee: 0,
      partnerAmount: 0,
      bookings: 0,
      details: [],
    };
    current.gross = moneyRound(current.gross + row.gross);
    current.discount = moneyRound(current.discount + row.discount);
    current.stripeFee = moneyRound(current.stripeFee + row.stripeFee);
    current.partnerAmount = moneyRound(current.partnerAmount + row.net);
    current.bookings += 1;
    current.details.push(row);
    grouped.set(key, current);
  });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      payment: payouts?.[row.partnerUid]?.[row.payoutId] || null,
    }))
    .sort((a, b) =>
      `${a.eventName}|${a.partnerName}`.localeCompare(
        `${b.eventName}|${b.partnerName}`,
        "pt",
        { sensitivity: "base" },
      ),
    );
}

export const partnerPaymentMethodLabel = (method, language = "pt") => {
  const en = String(language || "").toLowerCase().startsWith("en");
  return ({
    bank_transfer: en ? "Bank transfer" : "Transferência bancária",
    mbway: "MB WAY",
    cash: en ? "Cash" : "Numerário",
    other: en ? "Other" : "Outro",
  })[String(method || "")] || (en ? "Not specified" : "Não indicado");
};
